import { formatCurrency, formatSubscriptionAmount } from '@/utils/currency';

import { useSubscription } from '@chainlit/react-client';

import { useTranslation } from '@/components/i18n/Translator';
import { Card, CardContent } from '@/components/ui/card';

import BillingHistoryTable from './BillingHistoryTable';
import SubscriptionCard from './SubscriptionCard';

interface SubscriptionData {
  plan: string;
  status: 'active' | 'pending' | 'cancelled' | 'expired' | 'trialing';
  nextBilling: string;
  amount: string;
  features?: string[];
}

interface BillingHistoryItem {
  id: string;
  date: string;
  amount: string;
  status: string;
  invoice: string;
  description?: string;
  downloadUrl?: string;
}

interface BillingSectionProps {
  onNavigateToUpgrade?: () => void;
}

const BillingSection = ({ onNavigateToUpgrade }: BillingSectionProps = {}) => {
  const {
    isLoading,
    isError,
    billingHistory,
    isBillingLoading,
    isProUser,
    planInfo,
    createCustomerPortal
  } = useSubscription();

  const { t } = useTranslation();

  // Handle loading states
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('billing.title')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('billing.description')}
          </p>
        </div>
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center p-8">
              <div className="text-muted-foreground">
                {t('billing.loading')}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle error states
  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('billing.title')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('billing.description')}
          </p>
        </div>
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center p-8">
              <div className="text-destructive">{t('billing.error')}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Convert API data to component format
  const subscriptionData: SubscriptionData = {
    plan: planInfo.name,
    status:
      planInfo.status === 'active'
        ? 'active'
        : planInfo.status === 'trialing'
        ? 'trialing'
        : planInfo.status === 'canceled'
        ? 'cancelled'
        : planInfo.status === 'past_due'
        ? 'pending'
        : 'expired',
    nextBilling: planInfo.currentPeriodEnd || '',
    amount: planInfo.amount
      ? formatSubscriptionAmount(
          planInfo.amount,
          planInfo.currency || 'usd',
          planInfo.billingCycle || 'month',
          false,
          'sv'
        )
      : t('billing.amount.free'),
    features: isProUser
      ? [
          t('billing.features.pro'),
          t('billing.features.prioritySupport'),
          t('billing.features.advancedAnalytics')
        ]
      : [t('billing.features.basicFeatures')]
  };

  const billingHistoryData: BillingHistoryItem[] = billingHistory.map(
    (invoice) => ({
      id: invoice.id,
      date: new Date(invoice.date).toLocaleDateString(),
      amount: formatCurrency(
        invoice.amount,
        invoice.currency || planInfo.currency || 'usd'
      ),
      status:
        invoice.status === 'paid'
          ? t('billing.status.paid')
          : invoice.status === 'open'
          ? t('billing.status.pending')
          : invoice.status === 'void'
          ? t('billing.status.refunded')
          : t('billing.status.failed'),
      invoice: t('billing.invoice.prefix', { number: invoice.id.slice(-8) }),
      description:
        invoice.description ||
        t('billing.invoice.description', { planName: planInfo.name }),
      downloadUrl: invoice.invoice_pdf || invoice.hosted_invoice_url
    })
  );

  const handleManageSubscription = async () => {
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      const { portal_url } = await createCustomerPortal(currentUrl, 'sv');
      window.open(portal_url, '_blank');
    } catch (error) {
      console.error('Failed to open customer portal:', error);
    }
  };

  const handleUpgradeForFreeUser = () => {
    if (onNavigateToUpgrade) {
      onNavigateToUpgrade();
    }
  };

  const handleDownloadInvoice = (invoiceId: string) => {
    const invoice = billingHistory.find((inv) => inv.id === invoiceId);
    if (invoice?.invoice_pdf) {
      window.open(invoice.invoice_pdf, '_blank');
    } else if (invoice?.hosted_invoice_url) {
      window.open(invoice.hosted_invoice_url, '_blank');
    }
  };

  const handleViewInvoice = (invoiceId: string) => {
    const invoice = billingHistory.find((inv) => inv.id === invoiceId);
    if (invoice?.hosted_invoice_url) {
      window.open(invoice.hosted_invoice_url, '_blank');
    } else if (invoice?.invoice_pdf) {
      window.open(invoice.invoice_pdf, '_blank');
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {t('billing.title')}
        </h2>
        <p className="text-sm text-gray-600 mt-1">{t('billing.description')}</p>
      </div>

      <div className="space-y-6">
        {/* Subscription Details */}
        <SubscriptionCard
          subscription={subscriptionData}
          onManageSubscription={
            isProUser ? handleManageSubscription : undefined
          }
          onChangePayment={isProUser ? handleManageSubscription : undefined}
          onUpgrade={
            isProUser ? handleManageSubscription : handleUpgradeForFreeUser
          }
          isProUser={isProUser}
        />

        {/* Billing History */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('billing.history.title')}
          </h3>
          <BillingHistoryTable
            items={billingHistoryData}
            onDownloadInvoice={handleDownloadInvoice}
            onViewInvoice={handleViewInvoice}
            isLoading={isBillingLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default BillingSection;
